import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { v4 } from "uuid"
import { storage } from "./firebase"
import { logger } from "@/utils/logger"
import imageCompression from "browser-image-compression"

export async function uploadPhoto(file: File): Promise<string> {
   const options = {
      maxSizeMB: 0.1, // maximum file size in MegaBytes
      maxWidthOrHeight: 512, // maximum width or height in pixels
      useWebWorker: true, // use multi-thread web worker for better performance
      fileType: "image/webp",
   }
   
   try {
      // Compress the image
      const compressedFile = await imageCompression(file, options)
      logger("Original file size:", file.size / 1024 / 1024, "MB")
      logger("Compressed file size:", compressedFile.size / 1024 / 1024, "MB")
      
      // Upload the compressed image
      const filename = `${v4()}-${v4()}`
      const storageRef = ref(storage, `photos/${filename}`)
      logger("Uploading to " + storageRef.fullPath, compressedFile)
      await uploadBytes(storageRef, compressedFile)
      return getDownloadURL(storageRef)
   } catch (error) {
      logger("Error compressing or uploading image:", error)
      throw error
   }
}

export async function uploadFile(file: File, userId: string): Promise<string> {
   const filename = `${v4()}-${v4()}`
   const storageRef = ref(storage, `files/${userId}/${filename}`)
   logger("Uploading to " + storageRef.fullPath, file)
   await uploadBytes(storageRef, file)
   return getDownloadURL(storageRef)
}


/**
 * Récupère une image à partir de son URL Firebase Storage et la convertit en File
 * @param photoUrl URL de l'image à récupérer
 * @returns Un objet File contenant l'image
 */
export async function getImageFileFromUrl(photoUrl: string): Promise<File> {
   try {
      // Extraire le chemin de l'image à partir de l'URL
      // Les URLs Firebase Storage ressemblent généralement à:
      // https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[path]?alt=media&token=[token]
      const urlObj = new URL(photoUrl);
      const path = decodeURIComponent(urlObj.pathname.split('/o/')[1]?.split('?')[0]);
      
      if (!path) {
         throw new Error("Impossible d'extraire le chemin de l'image à partir de l'URL");
      }
      
      // Créer une référence à l'image dans Firebase Storage
      const imageRef = ref(storage, path);      
      
      // Récupérer l'URL de téléchargement (pour s'assurer d'avoir un token valide)
      const url = await getDownloadURL(imageRef);
      
      // Utiliser XMLHttpRequest au lieu de fetch pour contourner les problèmes CORS
      return new Promise((resolve, reject) => {
         const xhr = new XMLHttpRequest();
         xhr.responseType = 'blob';
         
         xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
               const blob = xhr.response;
               const fileName = path.split('/').pop() || 'profile.jpg';
               
               // Créer un objet File à partir du blob
               const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
               resolve(file);
            } else {
               reject(new Error(`Erreur HTTP: ${xhr.status}`));
            }
         };
         
         xhr.onerror = () => {
            reject(new Error("Erreur réseau lors du téléchargement de l'image"));
         };
         
         xhr.open('GET', url);
         xhr.send();
      });
   } catch (error) {
      logger("Erreur lors de la récupération de l'image:", error);
      throw error;
   }
}

/**
 * Récupère une image à partir de son URL et la convertit en File en utilisant directement l'URL
 * Cette fonction est utilisée comme solution de secours si getImageFileFromUrl échoue
 * @param photoUrl URL de l'image à récupérer
 * @returns Un objet File contenant l'image ou null si l'opération échoue
 */
export async function getImageFileFromUrlFallback(photoUrl: string): Promise<File | null> {
   try {
      // Créer un élément image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Créer une promesse pour attendre le chargement de l'image
      const imageLoaded = new Promise<HTMLImageElement>((resolve, reject) => {
         img.onload = () => resolve(img);
         img.onerror = () => reject(new Error("Impossible de charger l'image"));
         
         // Ajouter un timestamp pour éviter la mise en cache
         img.src = `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      });
      
      // Attendre que l'image soit chargée
      const loadedImg = await imageLoaded;
      
      // Créer un canvas pour dessiner l'image
      const canvas = document.createElement('canvas');
      canvas.width = loadedImg.width;
      canvas.height = loadedImg.height;
      
      // Dessiner l'image sur le canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
         throw new Error("Impossible de créer le contexte 2D");
      }
      
      ctx.drawImage(loadedImg, 0, 0);
      
      // Obtenir un blob à partir du canvas
      const blob = await new Promise<Blob | null>((resolve) => {
         canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      });
      
      if (!blob) {
         return null;
      }
      
      // Créer un fichier à partir du blob
      const fileName = photoUrl.split('/').pop() || 'profile.jpg';
      return new File([blob], fileName, { type: 'image/jpeg' });
   } catch (error) {
      logger("Erreur lors de la récupération de l'image (fallback):", error);
      return null;
   }
}

